import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "@tanstack/react-router";
import { Icon } from "./Icon";
import { PriorityChip } from "./PriorityChip";
import { AvatarGroup } from "./AvatarGroup";
import { TagChip } from "./TagChip";
import { parseDateLocal } from "@/lib/format";
import { formatEffort, type EffortUnit } from "@/lib/effort";
import { typeMeta } from "@/lib/workItemType";
import type { WorkItemType } from "@shared/types";

/**
 * TaskCard — design parity with Board.html .kanban-card.
 *
 * Renders: title + priority chip, tag chips with color dot, subtask
 * progress bar (N/M), multi-assignee avatar group, comment count,
 * attachment count, due-date pill with overdue/due-soon states.
 */

export type TaskCardTask = {
  id: string;
  title: string;
  /** Numeric priority: 1=urgent, 2=high, 3=medium, 4=low */
  priority: number;
  status: "backlog" | "todo" | "doing" | "done";
  assigneeIds: string[];
  tags?: { id: string; name: string; color: string }[];
  subtaskCount: number;
  subtaskCompletedCount: number;
  commentCount: number;
  attachmentCount: number;
  dueDate: string | null;
  estimate?: number | null;
  type?: WorkItemType;
  isBlocked?: boolean;
  sortOrder?: number;
};

type UsersMap = Record<string, { name: string; image?: string | null }>;

export type TaskCardProps = {
  task: TaskCardTask;
  users?: UsersMap;
  /**
   * When true, the card renders as a static clone for use inside <DragOverlay>.
   * Skips useSortable wiring (the overlay is not itself a sortable item) and
   * skips the navigate-on-click handler.
   */
  isDragOverlay?: boolean;
  /**
   * When true, applies the `.kc-reject` class for a 500ms shake animation
   * — used by KanbanBoard to flash a card whose drop was rejected.
   */
  rejecting?: boolean;
  /** Workspace effort unit for the estimate badge; defaults to "points". */
  effortUnit?: EffortUnit;
};

/** Shared card body — both the sortable card and the DragOverlay clone render this. */
function CardBody({
  task,
  users,
  effortUnit = "points",
}: {
  task: TaskCardTask;
  users: UsersMap;
  effortUnit?: EffortUnit;
}) {
  const effortLabel = formatEffort(task.estimate, effortUnit);
  // Compute due-date class. parseDateLocal keeps YYYY-MM-DD strings on the
  // user's local calendar day; using `new Date(str)` directly would treat
  // the string as UTC midnight and flip the class boundary a day early.
  const now = Math.floor(Date.now() / 1000);
  const dueDate = parseDateLocal(task.dueDate);
  const dueSeconds =
    dueDate != null ? Math.floor(dueDate.getTime() / 1000) : null;
  let dueClass = "kc-due";
  if (dueSeconds != null) {
    if (dueSeconds < now) {
      dueClass = "kc-due overdue";
    } else if (dueSeconds < now + 86400 * 3) {
      dueClass = "kc-due due-soon";
    }
  }

  // Subtask progress percentage (0-100)
  const subtaskPct =
    task.subtaskCount > 0
      ? Math.round((task.subtaskCompletedCount / task.subtaskCount) * 100)
      : 0;

  return (
    <>
      {/* Top: title + priority chip (matches design's .kc-top) */}
      <div className="kc-top">
        <div className="kc-title">
          {task.type && task.type !== "task" && (
            <span
              className="kc-type"
              title={typeMeta(task.type).label}
              style={{ color: typeMeta(task.type).color, marginRight: 5 }}
            >
              <Icon name={typeMeta(task.type).icon} size={12} />
            </span>
          )}
          {task.title}
        </div>
        <PriorityChip level={task.priority} />
      </div>

      {/* Tag chips */}
      {task.tags && task.tags.length > 0 && (
        <div className="kc-tags">
          {task.tags.map((t) => (
            <TagChip key={t.id} name={t.name} color={t.color} />
          ))}
        </div>
      )}

      {/* Subtask progress — label row + bar row (matches design) */}
      {task.subtaskCount > 0 && (
        <>
          <div className="kc-prog-lab">
            <span>Subtasks</span>
            <span>
              {task.subtaskCompletedCount}/{task.subtaskCount}
            </span>
          </div>
          <div
            className="progress kc-prog"
            role="progressbar"
            aria-valuenow={task.subtaskCompletedCount}
            aria-valuemin={0}
            aria-valuemax={task.subtaskCount}
            aria-label="Subtask progress"
          >
            <i style={{ width: `${subtaskPct}%` }} />
          </div>
        </>
      )}

      {/* Footer: assignees + meta counts + due date */}
      <div className="kc-foot">
        <div className="kc-assignees">
          <AvatarGroup
            users={task.assigneeIds.map((id) => ({
              id,
              name: users[id]?.name ?? "?",
              image: users[id]?.image,
            }))}
            max={3}
            size={22}
          />
        </div>
        <div className="kc-meta">
          {task.isBlocked && (
            <span className="kc-blocked" title="Has an unfinished blocker">
              <Icon name="flag" size={12} /> Blocked
            </span>
          )}
          {dueSeconds != null && (
            <span className={dueClass}>
              <Icon name="clock" size={12} />
              {new Date(dueSeconds * 1000).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
            </span>
          )}
          {effortLabel && (
            <span className="kc-estimate" title="Estimate">
              <Icon name="clock" size={12} />
              {effortLabel}
            </span>
          )}
          {task.commentCount > 0 && (
            <span>
              <Icon name="chat" size={12} />
              {task.commentCount}
            </span>
          )}
          {task.attachmentCount > 0 && (
            <span>
              <Icon name="clip" size={12} />
              {task.attachmentCount}
            </span>
          )}
        </div>
      </div>
    </>
  );
}

/** Sortable in-column card. Uses useSortable; click navigates to the task. */
function SortableCard({
  task,
  users,
  rejecting,
  effortUnit,
}: {
  task: TaskCardTask;
  users: UsersMap;
  rejecting: boolean;
  effortUnit?: EffortUnit;
}) {
  const navigate = useNavigate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (!isDragging) {
          navigate({ to: "/tasks/$taskId", params: { taskId: task.id } });
        }
      }}
      className={`kanban-card${isDragging ? " is-dragging" : ""}${rejecting ? " kc-reject" : ""}`}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        // Fade the source card while the DragOverlay carries the visual clone.
        opacity: isDragging ? 0.3 : 1,
        cursor: isDragging ? "grabbing" : "pointer",
      }}
      role="button"
      aria-label={`Open task: ${task.title}`}
      data-testid={`task-card-${task.id}`}
    >
      <CardBody task={task} users={users} effortUnit={effortUnit} />
    </div>
  );
}

/** Static clone used inside <DragOverlay>. No sortable wiring, no click. */
function OverlayCard({
  task,
  users,
  effortUnit,
}: {
  task: TaskCardTask;
  users: UsersMap;
  effortUnit?: EffortUnit;
}) {
  return (
    <div className="kanban-card kc-overlay">
      <CardBody task={task} users={users} effortUnit={effortUnit} />
    </div>
  );
}

export function TaskCard({
  task,
  users = {},
  isDragOverlay = false,
  rejecting = false,
  effortUnit = "points",
}: TaskCardProps) {
  return isDragOverlay ? (
    <OverlayCard task={task} users={users} effortUnit={effortUnit} />
  ) : (
    <SortableCard
      task={task}
      users={users}
      rejecting={rejecting}
      effortUnit={effortUnit}
    />
  );
}

// Legacy type alias — callers using TaskCardData can stay on it until they migrate.
export type TaskCardData = TaskCardTask;
